/**
 * QueueCalendarView — vue calendrier de la file de publication
 * (PR3 — 2026-05-10).
 *
 * Vue 7 jours autour de today : un row par jour, jobs `scheduled`
 * affichés comme cards horodatées. Click sur un slot → ouvre le
 * `<QueueItemDrawer>` du parent via le prop `onSelect(id)`. Badge
 * rouge sur les slots en retard (`scheduled_for < now`).
 *
 * Pas de drag-drop natif HTML5 dans cette première version — le
 * reschedule passe par PATCH `/api/queue/{id}/scheduled-for` (cf
 * `useHumanizer.usePatchScheduledFor`). Itération future :
 * @dnd-kit/core sur la grille.
 */
import { useMemo } from 'react'

import Button from '@/components/ui/Button'
import { useQueueCalendar } from '@/hooks/useHumanizer'
import type { components } from '@/types/api-generated'

import './QueueCalendarView.css'

type CalendarItem = components['schemas']['QueueCalendarItem']

interface QueueCalendarViewProps {
  /** Nombre de jours autour de today (avant + après). Défaut 7. */
  rangeDays?: number
  /** Click sur un slot → ouvrir drawer détail. */
  onSelect?: (queueItemId: number) => void
}

function _isoUtcMidnight(d: Date): string {
  // Crée une ISO UTC à 00:00 du jour de `d` (en local).
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}T00:00:00Z`
}

function _formatTime(iso: string): string {
  try {
    const dt = new Date(iso)
    const h = String(dt.getHours()).padStart(2, '0')
    const m = String(dt.getMinutes()).padStart(2, '0')
    return `${h}:${m}`
  } catch {
    return iso
  }
}

function _formatDayLabel(d: Date): string {
  return d.toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  })
}

function _bucketByDay(
  items: CalendarItem[],
  fromDay: Date,
  toDay: Date,
): Map<string, CalendarItem[]> {
  const buckets = new Map<string, CalendarItem[]>()
  // Initialise vides pour chaque jour de la fenêtre — UX pour montrer
  // les jours sans publi.
  const cursor = new Date(fromDay)
  while (cursor <= toDay) {
    const key = _isoUtcMidnight(cursor).slice(0, 10)
    buckets.set(key, [])
    cursor.setDate(cursor.getDate() + 1)
  }
  for (const item of items) {
    const slot = typeof item.scheduled_for === 'string' ? item.scheduled_for : ''
    if (!slot) continue
    const dayKey = slot.slice(0, 10)
    const bucket = buckets.get(dayKey)
    if (bucket) bucket.push(item)
  }
  return buckets
}

export default function QueueCalendarView({ rangeDays = 7, onSelect }: QueueCalendarViewProps) {
  const fromDate = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() - Math.floor(rangeDays / 2))
    d.setHours(0, 0, 0, 0)
    return d
  }, [rangeDays])
  const toDate = useMemo(() => {
    const d = new Date(fromDate)
    d.setDate(d.getDate() + rangeDays)
    return d
  }, [fromDate, rangeDays])

  const fromIso = _isoUtcMidnight(fromDate)
  const toIso = _isoUtcMidnight(toDate)

  const calendarQuery = useQueueCalendar(fromIso, toIso)

  const buckets = useMemo(() => {
    if (!calendarQuery.data) return new Map<string, CalendarItem[]>()
    return _bucketByDay(calendarQuery.data.items, fromDate, toDate)
  }, [calendarQuery.data, fromDate, toDate])

  if (calendarQuery.isLoading) {
    return <p className="ui-text-muted">Chargement du calendrier…</p>
  }

  const now = new Date()

  return (
    <div className="queue-calendar-view">
      {Array.from(buckets.entries()).map(([dayKey, items]) => {
        const day = new Date(dayKey)
        return (
          <section key={dayKey} className="queue-calendar-day">
            <header className="queue-calendar-day-header">
              <h3 className="ui-h3 queue-calendar-day-title">{_formatDayLabel(day)}</h3>
              <span className="ui-text-muted">
                {items.length} {items.length > 1 ? 'jobs' : 'job'}
              </span>
            </header>
            {items.length === 0 ? (
              <p className="queue-calendar-day-empty ui-text-muted">Aucune publication ce jour.</p>
            ) : (
              <ul className="queue-calendar-slots">
                {items.map((item) => {
                  const slotIso = typeof item.scheduled_for === 'string' ? item.scheduled_for : ''
                  const isOverdue = slotIso ? new Date(slotIso) < now : false
                  return (
                    <li key={item.id}>
                      <Button
                        type="button"
                        variant="ghost"
                        className={
                          isOverdue
                            ? 'queue-calendar-slot queue-calendar-slot--overdue'
                            : 'queue-calendar-slot'
                        }
                        onClick={() => onSelect?.(item.id)}
                      >
                        <span className="queue-calendar-slot-time">{_formatTime(slotIso)}</span>
                        <span className="queue-calendar-slot-platform">
                          {item.platform ?? item.job_kind}
                        </span>
                        <span className="queue-calendar-slot-title">
                          {item.work_title_fr ?? `Œuvre #${item.work_id}`}
                        </span>
                        {isOverdue ? (
                          <span className="queue-calendar-slot-badge">En retard</span>
                        ) : null}
                      </Button>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}
